import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { api } from "helpers/axios";
import { errorMessage, scrollInto } from "helpers/common";
import { toast } from "helpers/toast";
import { fetchFields, getAllFields, getBalancedScorecardFields, getCalculationRelatedFields, getConditionalFields, getFixedFields, getFreeFields, getSystemFields } from "store/fieldsSlice";
import * as Sentry from "@sentry/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { getModule } from "store/userSlice";
import Link from "components/ui/Link";
import { isDbModule } from "helpers/module";
import { Button, NativeSelect } from "components/ui";

function FormatSettings() {
  const dispatch = useDispatch();

  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const allFields = useSelector(getAllFields);

  const formatFields = useMemo(() => {
    if (allFields) {
      return allFields.filter(f => f.decimal !== null);
    }
    return [];
  }, [allFields]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const theadRef = useRef(null);

  const [isEditMode, setIsEditMode] = useState(false);

  const { register, getValues, setValue, watch } = useForm();

  const watchFields = watch('fields');

  useEffect(() => {
    if (formatFields.length > 0) {
      const obj = {};

      for (const field of formatFields) {
        obj[field.hash_id] = {
          decimal: field.decimal + '',
          fill_up: field.fill_up
        };
      }

      setValue('fields', obj);
    }
  }, [formatFields]);

  const handleSave = async () => {
    setError(null);
    setLoading(true);

    const formData = getValues();

    try {
      const { status } = await api.patch(`/settings/fields/format`, formData);

      if (status === 200) {
        setIsEditMode(false);

        dispatch(fetchFields());

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save number format'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  return <div data-testid="number-format-section">
    <div className="d-flex justify-content-between mb-3">
      <div>
        <h4>{editableTranslate('display_options.number_format_and_validation_settings', 'Zahlenformat und Validierungseinstellungen')}</h4>
        <p className="mb-0 pr-5" dangerouslySetInnerHTML={{ __html: editableTranslateString('display_options.number_format_and_validation_settings_description') }} />
      </div>
      <div className="text-nowrap">
        {
          !isEditMode && <Button variant="secondary" onClick={() => setIsEditMode(true)} data-testid="edit-button"><i className="bi bi-pencil-square"></i> {editableTranslate('global.edit')}</Button>
        }
        {
          isEditMode && <>
            <Button variant="secondary" onClick={() => setIsEditMode(false)} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
            <Button variant="primary" loading={loading} onClick={handleSave} data-testid="save-button">{editableTranslate('button.save')}</Button>
          </>
        }
      </div>
    </div>
    <div className="settings-form settings-employee">
      {
        error && <div>
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </div>
      }

      <div>
        <div className="table-responsive">
          <table className="table display-options-table w-50">
            <thead>
              <tr>
                <th>{editableTranslate('form.fields')}</th>
                <th className="text-center">{editableTranslate('display_options.decimals')}</th>
                <th className="text-center">{editableTranslate('display_options.fill_up')} <i className="bi bi-info-circle text-smaller" data-tooltip-id="fill-up-tooltip"></i></th>
              </tr>
            </thead>
            <tbody>
              {
                formatFields.map((field, i) => <tr key={i} className={`group-field-row ${i === formatFields.length - 1 ? 'last-row' : ''}`} data-testid={`row-${field?.hash_id}`}>
                  <td className="v-middle text-nowrap">{field?.custom_name || editableTranslate(field.translation_key, field.name)}</td>
                  <td className="text-center v-middle">
                    {
                      isEditMode ? <NativeSelect size="sm" className="w-auto mx-auto" {...register(`fields.${field.hash_id}.decimal`)}>
                        <option>0</option>
                        <option>1</option>
                        <option>2</option>
                        <option>3</option>
                      </NativeSelect> :
                        <span>{field.decimal}</span>
                    }
                  </td>
                  <td className="text-center v-middle">
                    {
                      isEditMode ? <input className={`mx-auto`} type="checkbox" {...register(`fields.${field.hash_id}.fill_up`)} disabled={watchFields?.[field.hash_id]?.decimal == 0} data-testid={`field-fill-up-${field.hash_id}`} /> :
                        <i className={`bi bi-check-circle-fill text-big ${field.fill_up ? '' : 'd-none'}`}></i>
                    }
                  </td>
                </tr>)
              }
            </tbody>
          </table>
        </div>
        <ReactTooltip id="fill-up-tooltip" content={translate('tooltips.display_options.fill_up_tooltip')} place="bottom" />
      </div>
    </div>
  </div>;
}

export default function DisplayOptions() {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const allFields = useSelector(getAllFields);
  const fixedFields = useSelector(getFixedFields);
  const calculationRelatedFields = useSelector(getCalculationRelatedFields);
  const systemFields = useSelector(getSystemFields);
  const conditionalFields = useSelector(getConditionalFields);
  const lkFields = useSelector(getBalancedScorecardFields);
  const freeFields = useSelector(getFreeFields);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const theadRef = useRef(null);
  const headerRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const [isEditMode, setIsEditMode] = useState(false);

  const [showSection, setShowSection] = useState({
    basic_data: true,
    state_employee_data: true,
    calculation_related: true,
    balanced_scorecard: true,
    additional_data: true
  });

  const maxVisibleFields = 15;
  const balancedScorecardMaxFields = 6;
  // Fields offered as toggles in the Leistungskarte column (FB-321/322). Nachname
  // and Vorname are fixed-on (isFixedChecked); everything not listed here is not
  // offered. Personalnummer is optional here but stays fixed-on in the other views.
  const balancedScorecardOptionalFields = [
    'field.employee_number', 'field.employment_entry_date', 'field.department', 'field.gender',
    'field.working_hours', 'field.pay_grade', 'field.pay_grade_level',
    'field.budget', 'field.group', 'field.result', 'field.personal_interviews',
  ];

  const sections = useMemo(() => {
    const obj = {
      employee_management: [],
      employee_data: [],
      rating_overview: [],
      balanced_scorecard: []
    };

    if (!isDbModuleActive) {
      obj.calculation_result = [];
    }

    return obj;
  }, [isDbModuleActive]);

  const displayKey = useMemo(() => {
    if (isDbModuleActive) return 'state_display_in';
    return 'display_in';
  }, [isDbModuleActive]);

  const sectionKeys = useMemo(() => {
    return Object.keys(sections);
  }, [sections]);

  const fixedChecked = ['field.last_name', 'field.first_name', 'field.employee_number'];
  const [fixedCheckedEmployeeData, setFixedCheckedEmployeeData] = useState([]);
  const fixedCheckedEmployeeOverview = useMemo(() => {
    const arr = ['field.working_hours', 'field.balanced_scorecard_template', 'field.budget', 'field.group'];
    return !isDbModuleActive ? arr : arr.slice(1);
  }, [isDbModuleActive]);
  const fixedCheckedRatingOverview = ['field.group'];

  const combinedCalculationRelatedFields = useMemo(() => {
    if (calculationRelatedFields && systemFields && conditionalFields) return [...calculationRelatedFields, ...systemFields, ...conditionalFields];

    return null;
  }, [calculationRelatedFields, systemFields, conditionalFields]);

  const filteredFixedFields = useMemo(() => {
    if (!fixedFields) return [];

    return fixedFields.filter(f => f.is_active && (isEditMode ? true : !['field.last_name', 'field.first_name', 'field.employee_number'].includes(f.translation_key) && f?.[displayKey].length > 0));
  }, [fixedFields, isEditMode, displayKey]);

  const filteredCalculationRelatedFields = useMemo(() => {
    if (!combinedCalculationRelatedFields) return [];

    return combinedCalculationRelatedFields.filter(f => f.is_active && (isEditMode ? true : !['field.exclude_from_payout'].includes(f.translation_key) && f?.[displayKey].length > 0) && f?.source !== 'state_employee_config');
  }, [combinedCalculationRelatedFields, isEditMode, displayKey]);

  const filteredStateEmployeeFields = useMemo(() => {
    if (!conditionalFields) return [];

    return conditionalFields.filter(f => {
      let hasDuplicate = false;
      for (const field of allFields) {
        if (field.slug === f.slug && field.hash_id !== f.hash_id) {
          hasDuplicate = true;
          break;
        }
      }

      return f.is_active && f?.source === 'state_employee_config' && f?.is_importable && !hasDuplicate;
    });
  }, [conditionalFields, allFields, isEditMode]);

  const filteredLkFields = useMemo(() => {
    if (!lkFields) return [];

    return lkFields.filter(f => f.is_active && (isEditMode ? true : !['field.balanced_scorecard_template', 'field.budget', 'field.group', 'field.lk'].includes(f.translation_key) && f?.[displayKey].length > 0));
  }, [lkFields, isEditMode, displayKey]);

  const filteredFreeFields = useMemo(() => {
    if (!freeFields) return [];

    return freeFields.filter(f => f.is_active && (isEditMode ? true : f?.[displayKey].length > 0));
  }, [freeFields, isEditMode, displayKey]);

  const { register, getValues, setValue, watch } = useForm({
    defaultValues: {
      display_options: { ...sections }
    }
  });

  const watchDisplayOptionsFields = watch('display_options');

  useEffect(() => {
    dispatch(fetchFields());

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const headerEl = headerRef.current;
    if (!headerEl) return;

    const handleScrollTable = () => {
      const parentEl = headerEl.parentElement;
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;
      if (coordinates.y < navHeight) {
        headerEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight + 7}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight + 7}px, 0)`;

        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight - 7}px, 0)`;
        floatingEl.style.height = (headerEl.offsetHeight + theadEl.offsetHeight + 21 + 7 + 14) + 'px';
      } else {
        headerEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';

        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, []);

  useEffect(() => {
    const arr = ['field.exclude_from_payout', 'field.balanced_scorecard_template', 'field.budget', 'field.group'];

    if (!isDbModuleActive) {
      arr.unshift('field.working_hours');
    }

    if (systemFields) {
      arr.push(...systemFields.map(f => f.translation_key));
    }

    if (conditionalFields) {
      arr.push(...conditionalFields.filter(f => f?.source !== 'state_employee_config').map(f => f.translation_key));
    }

    setFixedCheckedEmployeeData(arr);
  }, [isDbModuleActive && systemFields, conditionalFields]);

  const resetDisplayOptions = () => {
    if (fixedFields && combinedCalculationRelatedFields && lkFields && freeFields) {
      sectionKeys.forEach(section => {
        const arr = [];
        [...fixedFields, ...combinedCalculationRelatedFields, ...lkFields, ...freeFields].forEach((f, i) => {
          if (f?.[displayKey].includes(section) || (fixedChecked.includes(f.translation_key) && !(section === 'balanced_scorecard' && f.translation_key === 'field.employee_number')) || (fixedCheckedEmployeeData.includes(f.translation_key) && section === 'employee_data'))
            arr.push(f.hash_id);
        });

        setValue(`display_options.${section}`, arr, { shouldDirty: true });
      });
    }
  };

  useEffect(resetDisplayOptions, [fixedFields, combinedCalculationRelatedFields, lkFields, freeFields, sectionKeys, displayKey]);

  const handleSaveDisplayOptions = async () => {
    setError(null);
    setLoading(true);

    const values = getValues();

    try {
      for (const section in values.display_options) {
        const formData = {
          section,
          fields: []
        };

        fixedFields.filter(f => f.is_active).forEach(field => {
          const checked = values.display_options[section].includes(field.hash_id);
          const fixedChecked = isFixedChecked(field, section);
          const hidden = isFixedFieldHidden(field, section);

          if (fixedChecked || (checked && !hidden)) {
            formData.fields.push({ hash_id: field.hash_id, action: 'activate' });
          } else {
            formData.fields.push({ hash_id: field.hash_id, action: 'deactivate' })
          }
        });

        combinedCalculationRelatedFields.filter(f => f.is_active).forEach(field => {
          const checked = values.display_options[section].includes(field.hash_id);
          const fixedChecked = isFixedChecked(field, section);
          const hidden = isCalculationFieldHidden(field, section);

          if (fixedChecked || (checked && !hidden) || (checked && field?.source === 'state_employee_config')) {
            formData.fields.push({ hash_id: field.hash_id, action: 'activate' });
          } else {
            formData.fields.push({ hash_id: field.hash_id, action: 'deactivate' })
          }
        });

        lkFields.filter(f => f.is_active).forEach(field => {
          const checked = values.display_options[section].includes(field.hash_id);
          const fixedChecked = isFixedChecked(field, section);
          const hidden = isLkFieldHidden(field, section);

          if (fixedChecked || (checked && !hidden)) {
            formData.fields.push({ hash_id: field.hash_id, action: 'activate' });
          } else {
            formData.fields.push({ hash_id: field.hash_id, action: 'deactivate' })
          }
        });

        freeFields.filter(f => f.is_active).forEach(field => {
          const checked = values.display_options[section].includes(field.hash_id);
          const fixedChecked = isFixedChecked(field, section);

          if (fixedChecked || checked) {
            formData.fields.push({ hash_id: field.hash_id, action: 'activate' });
          } else {
            formData.fields.push({ hash_id: field.hash_id, action: 'deactivate' })
          }
        });

        if (formData.fields.length === 0) continue;

        await api.patch(`/settings/fields/display-option`, formData);
      }

      setLoading(false);
      setIsEditMode(false);

      dispatch(fetchFields());

      toast.success(translate('form.success.saved'));
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err, 'save display options'));
      scrollInto(`.display-options-div`, false, -100);
      Sentry.captureException(err);
    }
  };

  const isFixedChecked = (field, section) => {
    // Personalnummer is fixed-on everywhere except the Leistungskarte, where it is optional.
    if (section === 'balanced_scorecard' && field.translation_key === 'field.employee_number') return false;
    return fixedChecked.includes(field.translation_key) ||
      (fixedCheckedEmployeeData.includes(field.translation_key) && section === 'employee_data') ||
      (fixedCheckedEmployeeOverview.includes(field.translation_key) && section === 'employee_management') ||
      (fixedCheckedRatingOverview.includes(field.translation_key) && section === 'rating_overview');
  };

  const isFixedFieldHidden = (field, section) => {
    if (section === 'balanced_scorecard') return !isFixedChecked(field, section) && !balancedScorecardOptionalFields.includes(field.translation_key);
    return section === 'rating_overview' && !isFixedChecked(field, section);
  };

  const isCalculationFieldHidden = (field, section) => {
    if (section === 'balanced_scorecard') return !balancedScorecardOptionalFields.includes(field.translation_key);
    return section === 'rating_overview' && !isDbModuleActive && field.translation_key !== 'field.working_hours';
  };

  const isLkFieldHidden = (field, section) => {
    if (section === 'balanced_scorecard') return !balancedScorecardOptionalFields.includes(field.translation_key);
    return section === 'rating_overview' && ['field.fixed_payout', 'field.pay_factor'].includes(field.translation_key);
  };

  const handleReadonly = (e) => {
    e.preventDefault();
    return false;
  };

  const showHideSection = (section) => {
    setShowSection(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          {
            !isDbModuleActive && <FormatSettings />
          }

          <div className="display-options-div" data-testid="visible-fields-section">
            <div className="position-relative">
              <div ref={floatingSeparatorRef} className="floating-separator"></div>
            </div>
            <div ref={headerRef} className={`${isDbModuleActive ? '' : 'my-4'} mb-3 position-relative z-99`}>
              <div className="d-flex justify-content-between">
                <div>
                  <h4>{editableTranslate('display_options.visible_fields', 'Sichtbare Felder')}</h4>
                  <p className="mb-1">{editableTranslate('free_fields.display_options.description')}</p>
                  <span>{editableTranslate('display_options.go_to_field_configuration_description', 'Zum Aktivieren oder Deaktivieren von Feldern gehen Sie zu')} <Link data-testid="field-configuration-link" to="/system-settings/field-configuration">{editableTranslate('global.menu.settings.field_configuration')}</Link>.</span>
                </div>
                <div className="text-nowrap">
                  {
                    !isEditMode && <Button variant="secondary" onClick={() => setIsEditMode(true)} data-testid="edit-button"><i className="bi bi-pencil-square"></i> {editableTranslate('global.edit')}</Button>
                  }
                  {
                    isEditMode && <>
                      <Button variant="secondary" onClick={() => { resetDisplayOptions(); setIsEditMode(false); }} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                      <Button variant="primary" loading={loading} onClick={handleSaveDisplayOptions} data-testid="save-button">{editableTranslate('button.save')}</Button>
                    </>
                  }
                </div>
              </div>
              {
                error && <div className="mt-3">
                  <ErrorAlert error={error} onClose={() => setError(null)} />
                </div>
              }
            </div>
          </div>
          <div className="settings-form settings-employee">
            <div>
              <div className="table-responsive">
                <table className="table display-options-table">
                  <thead ref={theadRef} className="sticky-top">
                    <tr>
                      <th className="v-middle">{editableTranslate('form.fields')}</th>
                      {
                        sectionKeys.map((section, index) => <th key={index} className="align-text-top text-center">
                          <span className="d-block font-weight-bold text-big">{editableTranslate(section === 'balanced_scorecard' && isDbModuleActive ? 'free_fields.display_options.evaluation' : 'free_fields.display_options.' + section)}</span>
                          {
                            section !== 'employee_data' && <span className="font-weight-normal">{editableTranslate(section === 'balanced_scorecard' ? 'free_fields.display_options.select_balanced_scorecard_fields_description' : 'free_fields.display_options.select_fields_description', null, section === 'balanced_scorecard' ? { count: balancedScorecardMaxFields } : {})}</span>
                          }
                        </th>)
                      }
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="no-border-top" colSpan={sectionKeys.length + 1}></td>
                    </tr>
                    <tr>
                      <td colSpan={sectionKeys.length + 1} className="group-title font-weight-bold">
                        <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.basic_data ? 'up' : 'down'}`} onClick={() => showHideSection('basic_data')} data-testid="toggle-section-basic-data" />
                        {editableTranslate('employee.basic_data')}
                      </td>
                    </tr>
                    {
                      showSection?.basic_data && filteredFixedFields && filteredFixedFields.map((field, i) => <tr key={i} className={`group-field-row ${i === filteredFixedFields.length - 1 ? 'last-row' : ''}`} data-testid={`row-${field?.hash_id}`}>
                        <td>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</td>
                        {
                          sectionKeys.map((section, j) => <td key={j} className="v-middle text-center">
                            {
                              isEditMode ? <input className={`mx-auto ${isFixedChecked(field, section) ? 'readonly-check' : ''} ${isFixedFieldHidden(field, section) ? 'invisible' : ''}`} type="checkbox" value={field.hash_id} data-testid={`display-option-${section}-${field.hash_id}`} {...register(`display_options.${section}`)} onClick={isFixedChecked(field, section) ? handleReadonly : null}
                                disabled={section !== 'employee_data' && !watchDisplayOptionsFields[section].includes(field.hash_id) && watchDisplayOptionsFields[section].length >= (section === 'balanced_scorecard' ? balancedScorecardMaxFields : maxVisibleFields)} /> :
                                (
                                  !isFixedFieldHidden(field, section) && (watchDisplayOptionsFields[section].includes(field.hash_id) ? <i className="bi bi-check-circle-fill text-big"></i> : <span>--</span>)
                                )
                            }
                          </td>)
                        }
                      </tr>)
                    }

                    {
                      filteredStateEmployeeFields.length > 0 && <>
                        <tr>
                          <td className="no-border-top" colSpan={sectionKeys.length + 1}></td>
                        </tr>
                        <tr>
                          <td colSpan={sectionKeys.length + 1} className="group-title font-weight-bold">
                            <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.state_employee_data ? 'up' : 'down'}`} onClick={() => showHideSection('state_employee_data')} data-testid="toggle-section-state-employee-data" />
                            {editableTranslate('field_configuration.state_employee_data', 'Dienstliche Beurteilung (DB)')}
                          </td>
                        </tr>
                      </>
                    }
                    {
                      showSection?.state_employee_data && filteredStateEmployeeFields && filteredStateEmployeeFields.map((field, i) => <tr key={i} className={`group-field-row ${i === filteredStateEmployeeFields.length - 1 ? 'last-row' : ''}`} data-testid={`row-${field?.hash_id}`}>
                        <td>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</td>
                        {
                          sectionKeys.map((section, j) => <td key={j} className="v-middle text-center">
                            {
                              isEditMode ? <input className={`mx-auto ${isFixedChecked(field, section) ? 'readonly-check' : ''}`} type="checkbox" value={field.hash_id} data-testid={`display-option-${section}-${field.hash_id}`} {...register(`display_options.${section}`)} onClick={isFixedChecked(field, section) ? handleReadonly : null}
                                disabled={section !== 'employee_data' && !watchDisplayOptionsFields[section].includes(field.hash_id) && watchDisplayOptionsFields[section].length >= (section === 'balanced_scorecard' ? balancedScorecardMaxFields : maxVisibleFields)} /> :
                                (
                                  (watchDisplayOptionsFields[section].includes(field.hash_id) ? <i className="bi bi-check-circle-fill text-big"></i> : <span>--</span>)
                                )
                            }
                          </td>)
                        }
                      </tr>)
                    }

                    {
                      filteredCalculationRelatedFields.length > 0 && <>
                        <tr>
                          <td className="no-border-top" colSpan={sectionKeys.length + 1}></td>
                        </tr>
                        <tr>
                          <td colSpan={sectionKeys.length + 1} className="group-title font-weight-bold">
                            <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.calculation_related ? 'up' : 'down'}`} onClick={() => showHideSection('calculation_related')} data-testid="toggle-section-calculation-related" />
                            {editableTranslate('employee.calculation_related')}
                          </td>
                        </tr>
                      </>
                    }
                    {
                      showSection?.calculation_related && filteredCalculationRelatedFields && filteredCalculationRelatedFields.map((field, i) => <tr key={i} className={`group-field-row ${i === filteredCalculationRelatedFields.length - 1 ? 'last-row' : ''}`} data-testid={`row-${field?.hash_id}`}>
                        <td>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</td>
                        {
                          sectionKeys.map((section, j) => <td key={j} className="v-middle text-center">
                            {
                              isEditMode ? <input className={`mx-auto ${isFixedChecked(field, section) ? 'readonly-check' : ''} ${isCalculationFieldHidden(field, section) ? 'invisible' : ''}`} type="checkbox" value={field.hash_id} data-testid={`display-option-${section}-${field.hash_id}`} {...register(`display_options.${section}`)} onClick={isFixedChecked(field, section) ? handleReadonly : null}
                                disabled={section !== 'employee_data' && !watchDisplayOptionsFields[section].includes(field.hash_id) && watchDisplayOptionsFields[section].length >= (section === 'balanced_scorecard' ? balancedScorecardMaxFields : maxVisibleFields)} /> :
                                (
                                  !isCalculationFieldHidden(field, section) && (watchDisplayOptionsFields[section].includes(field.hash_id) ? <i className="bi bi-check-circle-fill text-big"></i> : <span>--</span>)
                                )
                            }
                          </td>)
                        }
                      </tr>)
                    }

                    <tr>
                      <td className="no-border-top" colSpan={sectionKeys.length + 1}></td>
                    </tr>
                    <tr>
                      <td colSpan={sectionKeys.length + 1} className="group-title font-weight-bold">
                        <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.balanced_scorecard ? 'up' : 'down'}`} onClick={() => showHideSection('balanced_scorecard')} data-testid="toggle-section-balanced-scorecard" />
                        {editableTranslate(isDbModuleActive ? 'state_employee.rating_section_title' : 'employee.balanced_scorecard')}
                      </td>
                    </tr>
                    {
                      showSection?.balanced_scorecard && filteredLkFields && filteredLkFields.map((field, i) => <tr key={i} className={`group-field-row ${i === filteredLkFields.length - 1 ? 'last-row' : ''}`} data-testid={`row-${field?.hash_id}`}>
                        <td>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</td>
                        {
                          sectionKeys.map((section, j) => <td key={j} className="v-middle text-center">
                            {
                              isEditMode ? <input className={`mx-auto ${isFixedChecked(field, section) ? 'readonly-check' : ''} ${isLkFieldHidden(field, section) ? 'invisible' : ''}`} type="checkbox" value={field.hash_id} data-testid={`display-option-${section}-${field.hash_id}`} {...register(`display_options.${section}`)} onClick={isFixedChecked(field, section) ? handleReadonly : null}
                                disabled={section !== 'employee_data' && !watchDisplayOptionsFields[section].includes(field.hash_id) && watchDisplayOptionsFields[section].length >= (section === 'balanced_scorecard' ? balancedScorecardMaxFields : maxVisibleFields)} /> :
                                (
                                  !isLkFieldHidden(field, section) && (watchDisplayOptionsFields[section].includes(field.hash_id) ? <i className="bi bi-check-circle-fill text-big"></i> : <span>--</span>)
                                )
                            }
                          </td>)
                        }
                      </tr>)
                    }

                    {
                      filteredFreeFields.length > 0 && <>
                        <tr>
                          <td className="no-border-top" colSpan={sectionKeys.length + 1}></td>
                        </tr>
                        <tr>
                          <td colSpan={sectionKeys.length + 1} className="group-title font-weight-bold">
                            <Button variant="ghost" size="inline" className="mr-1" icon={`bi-chevron-${showSection?.additional_data ? 'up' : 'down'}`} onClick={() => showHideSection('additional_data')} data-testid="toggle-section-additional-data" />
                            {editableTranslate('employee.additional_data')}
                          </td>
                        </tr>
                      </>
                    }
                    {
                      showSection?.additional_data && filteredFreeFields && filteredFreeFields.map((field, i) => <tr key={i} className={`group-field-row ${i === filteredFreeFields.length - 1 ? 'last-row' : ''}`} data-testid={`row-${field?.hash_id}`}>
                        <td>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</td>
                        {
                          sectionKeys.map((section, j) => <td key={j} className="v-middle text-center">
                            {
                              isEditMode ? <input className={`mx-auto ${isFixedChecked(field, section) ? 'readonly-check' : ''}`} type="checkbox" value={field.hash_id} data-testid={`display-option-${section}-${field.hash_id}`} {...register(`display_options.${section}`)} onClick={isFixedChecked(field, section) ? handleReadonly : null}
                                disabled={section !== 'employee_data' && !watchDisplayOptionsFields[section].includes(field.hash_id) && watchDisplayOptionsFields[section].length >= (section === 'balanced_scorecard' ? balancedScorecardMaxFields : maxVisibleFields)} /> :
                                (
                                  watchDisplayOptionsFields[section].includes(field.hash_id) ? <i className="bi bi-check-circle-fill text-big"></i> : <span>--</span>
                                )
                            }
                          </td>)
                        }
                      </tr>)
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
